import React from "react"
import { useTranslation } from "react-i18next"

import { ProductPositionTypename, RoomTypename } from "shared/types/Project"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { TabBar } from "shared/ui/tab-bar/TabBar"
import { Tooltip } from "shared/ui/tooltip/Tooltip"

import {
  UNASSIGNED_PRODUCTS_TAB_ID,
  UNASSIGNED_ROOMS_TAB_ID
} from "entities/project-children/lib/projectTabIDs"

import {
  FloorTabRendererProps,
  ProjectPageFloorTabConnector
} from "./ProjectPageFloorTabConnector"
import { ProjectPageTabBarProps } from "./ProjectPageTabBar"
import { useProjectPageTabDropTarget } from "./lib/useProjectPageTabDropTarget"
import "./styles/_project-page-tab-bar.scss"

interface UnassignedTabProps {
  eventKey: string
  count: number
  icon: IconName
  tooltipContent: string
  ariaLabel: string

  onTabSelect: (eventKey: string) => void
  acceptTypes: string[]
  directDropAcceptTypes: string[]
}

const ROOMS = [RoomTypename]
const ROOMS_AND_PRODUCTS = [RoomTypename, ProductPositionTypename]
const PRODUCTS = [ProductPositionTypename]

const UnassignedTab = React.memo(
  ({
    eventKey,
    count,
    icon,
    tooltipContent,
    ariaLabel,
    onTabSelect,
    acceptTypes,
    directDropAcceptTypes
  }: UnassignedTabProps) => {
    const { dropRef, isOver, canDrop } = useProjectPageTabDropTarget({
      index: 0,
      label: ariaLabel,
      eventKey,
      onTabSelect,
      hoverDelay: 200,
      enabled: true,
      acceptTypes,
      directDropAcceptTypes
    })

    return (
      <Tooltip content={tooltipContent} placement="top">
        <div
          className="project-page-tab-bar__tab project-page-tab-bar__unassigned-tab"
          data-drop-zone=""
          data-drop-zone-hover={isOver ? "" : undefined}
          data-drop-zone-active={isOver && canDrop ? "" : undefined}
          ref={dropRef}
        >
          <TabBar.Tab
            index={-1}
            eventKey={eventKey}
            label={ariaLabel}
            isDragDisabled
            aria-label={ariaLabel}
            icon={icon}
            count={count}
          />
        </div>
      </Tooltip>
    )
  }
)

export interface ProjectPageTabBarViewModel
  extends Omit<ProjectPageTabBarProps, "onSelect" | "activeFloorID"> {
  activeKey: string
  onSelect: (eventKey: string) => void
  FloorTabRenderer?: React.ComponentType<FloorTabRendererProps>
}

export const ProjectPageTabBarView = React.memo(
  ({
    activeKey,
    onSelect,
    unassignedRoomsCount,
    unassignedProductsCount,
    floors,
    onDrop,
    ariaControls,
    onSelectFloor,
    truncationThreshold,
    onFloorSort,
    projectID,
    FloorTabRenderer = ProjectPageFloorTabConnector
  }: ProjectPageTabBarViewModel) => {
    const { t } = useTranslation()

    const hasUnassignedElements =
      unassignedProductsCount > 0 || unassignedRoomsCount > 0

    return (
      <TabBar.Root
        activeKey={activeKey}
        onSelect={onSelect}
        className="project-page-tab-bar card card--elevated"
        enableSort={!!onFloorSort}
        onSort={onFloorSort}
      >
        <div className="project-page-tab-bar__icon">
          <Icon name={IconName.FLOOR} />
        </div>
        {hasUnassignedElements ? (
          <div className="project-page-tab-bar__unassigned-tabs">
            {unassignedProductsCount > 0 && (
              <UnassignedTab
                eventKey={UNASSIGNED_PRODUCTS_TAB_ID}
                count={unassignedProductsCount}
                icon={IconName.PRODUCTS}
                tooltipContent={t("Floor.UnassignedProducts")}
                ariaLabel={t("Floor.UnassignedProducts")}
                onTabSelect={onSelect}
                acceptTypes={PRODUCTS}
                directDropAcceptTypes={PRODUCTS}
              />
            )}
            {unassignedRoomsCount > 0 && (
              <UnassignedTab
                eventKey={UNASSIGNED_ROOMS_TAB_ID}
                count={unassignedRoomsCount}
                icon={IconName.ROOM}
                tooltipContent={t("Floor.UnassignedRooms")}
                ariaLabel={t("Floor.UnassignedRooms")}
                onTabSelect={onSelect}
                acceptTypes={ROOMS_AND_PRODUCTS}
                directDropAcceptTypes={ROOMS}
              />
            )}
          </div>
        ) : null}

        <TabBar.ScrollGroup
          scrollDistance={200}
          className="project-page-tab-bar__floor-tabs"
        >
          <TabBar.ArrowLeft />
          <TabBar.ScrollContent>
            {floors.map((floor, index) => (
              <FloorTabRenderer
                key={floor.id}
                projectID={projectID}
                floor={floor}
                index={index}
                onSelectFloor={onSelectFloor}
                truncationThreshold={truncationThreshold}
                onDrop={onDrop}
                ariaControls={ariaControls}
              />
            ))}
          </TabBar.ScrollContent>
          <TabBar.ArrowRight />
        </TabBar.ScrollGroup>
      </TabBar.Root>
    )
  }
)
